import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Avatar,
    LinearProgress,
} from '@mui/material';
import {
    FlashOn,
    Speed,
    AccountBalanceWallet,
    CheckCircle,
    HourglassTop,
    CalendarToday,
    TrendingUp,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    formatCurrency,
    formatKwh,
    formatWatt,
    formatBillingPeriod,
    calculateBillingCycleProgress,
} from '../../utils/powerCostUtils';
import { getMonthName } from '../../utils/monthlyDataUtils';

function PowerCostKpiCards({ summary, selectedMonth, selectedYear }) {
    const { t } = useTranslation();

    if (!summary) return null;

    const isAllMonths = selectedMonth === 'all';
    const activeRecord = summary.latestRecord;

    // Billing cycle progress calculation for active record
    const cycleProgress = activeRecord
        ? calculateBillingCycleProgress(
              activeRecord.periodStart,
              activeRecord.periodEnd,
              activeRecord.actualCalculationEnd
          )
        : { progressPercent: 100, elapsedDays: 30, totalDays: 30, isCompleted: true };

    const isPartialCycle = activeRecord ? Boolean(activeRecord.isPartial) : false;

    return (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {/* Card 1: Total Estimated Cost */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                    elevation={2}
                    sx={{
                        height: '100%',
                        borderLeft: '4px solid #10b981',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}
                >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                {isAllMonths ? t('common.totalEstimatedCost', 'Total Cost (Year)') : t('common.totalCost', 'Total Amount Due')}
                            </Typography>
                            <Avatar
                                sx={{
                                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                                    color: '#10b981',
                                    width: 34,
                                    height: 34,
                                }}
                            >
                                <AccountBalanceWallet fontSize="small" />
                            </Avatar>
                        </Box>

                        <Typography variant="h4" component="div" sx={{ fontWeight: 800, color: '#10b981' }}>
                            {formatCurrency(summary.totalCost)}
                        </Typography>

                        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {isAllMonths
                                    ? `${t('common.monthlyAverageCost', 'Avg / Month')}: ${formatCurrency(summary.averageMonthlyCost)}`
                                    : `${t('common.baseEnergyCost', 'Base')}: ${formatCurrency(activeRecord?.baseEnergyCost)} | ${t('common.vatAmount', 'VAT')}: ${formatCurrency(activeRecord?.vatAmount)}`}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Card 2: Total Units Consumed */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                    elevation={2}
                    sx={{
                        height: '100%',
                        borderLeft: '4px solid #3b82f6',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}
                >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                {t('common.totalUnitsConsumed', 'Total Consumption')}
                            </Typography>
                            <Avatar
                                sx={{
                                    bgcolor: 'rgba(59, 130, 246, 0.12)',
                                    color: '#3b82f6',
                                    width: 34,
                                    height: 34,
                                }}
                            >
                                <FlashOn fontSize="small" />
                            </Avatar>
                        </Box>

                        <Typography variant="h4" component="div" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                            {formatKwh(summary.totalKwh)}
                        </Typography>

                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {isAllMonths
                                    ? `${t('common.monthlyAverageCost', 'Avg / Month')}: ${formatKwh(summary.averageKwh)}`
                                    : `${t('common.recordCount', '1 billing cycle')}`}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Card 3: Average Power */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                    elevation={2}
                    sx={{
                        height: '100%',
                        borderLeft: '4px solid #8b5cf6',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}
                >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                {t('common.averagePower', 'Average Power')}
                            </Typography>
                            <Avatar
                                sx={{
                                    bgcolor: 'rgba(139, 92, 246, 0.12)',
                                    color: '#8b5cf6',
                                    width: 34,
                                    height: 34,
                                }}
                            >
                                <Speed fontSize="small" />
                            </Avatar>
                        </Box>

                        <Typography variant="h4" component="div" sx={{ fontWeight: 800, color: '#8b5cf6' }}>
                            {formatWatt(summary.averagePowerWatt)}
                        </Typography>

                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {summary.peakMonthRecord && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TrendingUp sx={{ fontSize: 14, color: '#ef4444' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {t('common.peakMonth', 'Peak')}: {getMonthName(summary.peakMonthRecord.month, t)} ({formatCurrency(summary.peakMonthRecord.totalCost)})
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Card 4: Billing Cycle Status & Progress */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                    elevation={2}
                    sx={{
                        height: '100%',
                        borderLeft: `4px solid ${isPartialCycle ? '#f59e0b' : '#10b981'}`,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}
                >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                {t('common.billingCycleStatus', 'Billing Cycle Status')}
                            </Typography>

                            <Chip
                                icon={isPartialCycle ? <HourglassTop fontSize="small" /> : <CheckCircle fontSize="small" />}
                                label={isPartialCycle ? t('common.cyclePartial', 'In Progress') : t('common.cycleCompleted', 'Completed')}
                                size="small"
                                color={isPartialCycle ? 'warning' : 'success'}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    animation: isPartialCycle ? 'pulse 2s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1 },
                                        '50%': { opacity: 0.7 },
                                        '100%': { opacity: 1 },
                                    },
                                }}
                            />
                        </Box>

                        <Box sx={{ my: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('common.cycleProgress', {
                                        percent: cycleProgress.progressPercent,
                                        elapsed: cycleProgress.elapsedDays,
                                        total: cycleProgress.totalDays,
                                    })}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: isPartialCycle ? '#f59e0b' : '#10b981' }}>
                                    {cycleProgress.progressPercent}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={cycleProgress.progressPercent}
                                color={isPartialCycle ? 'warning' : 'success'}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>

                        {activeRecord && (
                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {formatBillingPeriod(activeRecord.periodStart, activeRecord.periodEnd)}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}

export default PowerCostKpiCards;
